import { MarkHearthArch as HearthMark } from "../brand/Marks";
import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, Mail, Check, Heart } from "lucide-react";
import { toast } from "sonner";
import { blogSubscribe, blogHelpful } from "../../lib/api";
import { Button } from "../ui/button";

// Double opt-in newsletter capture: server emails a confirmation link; /blog?subscribed=1 confirms.
export const NewsletterForm = ({ source = "blog", compact = false }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("");
  const [params] = useSearchParams();
  const flag = params.get("subscribed") || (params.get("unsubscribed") ? `unsub-${params.get("unsubscribed")}` : "");
  const submit = async (e) => {
    e.preventDefault();
    setState("busy");
    try {
      const r = await blogSubscribe(email.trim(), source);
      setState(r.status);
      if (r.status === "already") toast.success(r.message);
    } catch (err) {
      setState("");
      toast.error(err?.response?.data?.detail || "Couldn't subscribe");
    }
  };
  const banner = flag === "1" ? "You're subscribed — the next Field Notes lands in your inbox." : flag === "invalid" ? "That confirmation link isn't valid any more." : flag === "unsub-1" ? "You're unsubscribed. No hard feelings." : "";
  return (
    <section data-testid="newsletter-form" className={`rounded-3xl border border-border bg-card ${compact ? "p-5" : "p-6 sm:p-8"}`}>
      {banner && <div className="mb-3 rounded-xl bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 px-3 py-2 text-sm" data-testid="newsletter-banner">{banner}</div>}
      <div className="flex items-start gap-3">
        <span className="h-9 w-9 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0"><Mail className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-serif text-xl leading-snug">Get Field Notes by email</h3>
          <p className="mt-1 text-sm text-muted-foreground">One short read a week. No trackers, no selling your address, unsubscribe in one click.</p>
          {state === "pending" ? (
            <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300" data-testid="newsletter-pending"><Check className="h-4 w-4" /> Check your inbox and click the confirmation link.</div>
          ) : (
            <form onSubmit={submit} className="mt-3 flex flex-col sm:flex-row gap-2">
              <input data-testid="newsletter-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="flex-1 h-11 rounded-full border border-input bg-background px-4 text-sm" />
              <Button type="submit" data-testid="newsletter-submit" disabled={state === "busy"} className="rounded-full h-11 px-5 gap-2">{state === "busy" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Subscribe</Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

// End-of-post nudge: turn a reader into a vault. Tags the sign-up with the post so it shows in Admin traffic.
export const ReaderNudge = ({ post }) => (
  <section data-testid="reader-nudge" className="mt-10 rounded-3xl bg-foreground text-background p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-5">
    <span className="h-11 w-11 rounded-2xl bg-primary text-primary-foreground grid place-items-center shrink-0"><HearthMark className="h-5 w-5" /></span>
    <div className="flex-1">
      <p className="font-serif text-2xl leading-snug">Save this checklist to your vault.</p>
      <p className="mt-1 text-sm opacity-80">HearthVault files the documents behind it, reminds you before anything lapses, and hands the whole picture to your family when they need it. Free to start.</p>
    </div>
    <Button asChild size="lg" variant="secondary" className="rounded-full h-12 px-6 gap-2 shrink-0"><Link to={`/welcome?src=blog&post=${encodeURIComponent(post.slug)}`} data-testid="reader-nudge-cta">Create your vault <ArrowRight className="h-4 w-4" /></Link></Button>
  </section>
);

// "This helped" — one anonymous counter per post. Nothing identifying is sent; the tap is remembered only in this browser.
export const HelpfulButton = ({ post }) => {
  const key = `hv-helped-${post.slug}`;
  const [done, setDone] = useState(false);
  const [count, setCount] = useState(post.helpful_count || 0);
  const [busy, setBusy] = useState(false);
  useEffect(() => { try { setDone(localStorage.getItem(key) === "1"); } catch { /* private mode */ } }, [key]);
  const tap = async () => {
    if (done || busy) return;
    setBusy(true);
    try {
      const r = await blogHelpful(post.slug);
      setCount(r.helpful_count); setDone(true);
      try { localStorage.setItem(key, "1"); } catch { /* ignore */ }
    } catch { toast.error("Couldn't record that — try again in a moment"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-10 flex flex-wrap items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3" data-testid="helpful-box">
      <span className="text-sm">{done ? "Thanks — noted, anonymously." : "Was this note useful?"}</span>
      <button type="button" data-testid="helpful-btn" onClick={tap} disabled={done || busy} aria-pressed={done}
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${done ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary hover:text-primary"}`}>
        <Heart className={`h-4 w-4 ${done ? "fill-current" : ""}`} /> This helped{count > 0 && <span className="font-mono text-xs" data-testid="helpful-count">· {count}</span>}
      </button>
      <span className="text-xs text-muted-foreground ml-auto">No tracking — just a counter.</span>
    </div>
  );
};
